import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Switch,
    Tooltip,
} from '@mui/material';
import HistoryIcon from '@mui/icons-material/History';
import Swal from 'sweetalert2';
import { ExtendedGridColDef } from '@/types/ExtendedGridColDef';
import { SCUIContent } from '../../../atomic/organisms';
import { SCUIBreadCrumb, SCUICustomDataGrid } from '../../../atomic/molecules';
import { SCUILoadingLinear } from '../../../atomic/atoms';
import { PageUrl } from '../../../types/enums/pageUrl-enums';
import Helper from '../../../utils/helper';
import StoreManagerPermissionQueryService from '../../../services/query/storeManagerPermission/StoreManagerPermissionQuery.services';
import StoreManagerPermissionCommandService from '../../../services/command/storeManagerPermission/StoreManagerPermissionCommand.services';

const DEFAULT_FEATURE_KEY = 'StoreReturn';

interface StaffRow {
    applicationUserId: number;
    userRef: number;
    username: string;
    title: string;
    storeCode: string;
    featureKey: string;
    hasGrant: boolean;
    grantedByUsername: string;
    grantedAt: string | null;
    isUpdating?: boolean;
}

interface HistoryRow {
    historyId: number;
    actorUsername: string;
    targetUsername: string;
    featureKey: string;
    action: string;
    storeCode: string;
    createdDate: string;
}

export const StaffPermissions: FC = () => {
    const { t, i18n } = useTranslation();
    const [rows, setRows] = useState<StaffRow[]>([]);
    const [loading, setLoading] = useState(false);
    const [featureKey] = useState(DEFAULT_FEATURE_KEY);
    const storeCode = Helper.getStoreCode();

    const [historyOpen, setHistoryOpen] = useState(false);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [historyRows, setHistoryRows] = useState<HistoryRow[]>([]);
    const [historyUser, setHistoryUser] = useState<StaffRow | null>(null);
    const [historyPage, setHistoryPage] = useState(1);
    const [historyPageSize, setHistoryPageSize] = useState(10);
    const [historyTotalCount, setHistoryTotalCount] = useState(0);

    const mapStaffItem = (item: any): StaffRow => ({
        applicationUserId: item.applicationUserId ?? item.ApplicationUserId,
        userRef: item.userRef ?? item.UserRef,
        username: item.username ?? item.Username ?? '',
        title: item.title ?? item.Title ?? '',
        storeCode: item.storeCode ?? item.StoreCode ?? storeCode ?? '',
        featureKey: item.featureKey ?? item.FeatureKey ?? featureKey,
        hasGrant: item.hasGrant ?? item.HasGrant ?? false,
        grantedByUsername: item.grantedByUsername ?? item.GrantedByUsername ?? '',
        grantedAt: item.grantedAt ?? item.GrantedAt ?? null,
    });

    const mapHistoryItem = (item: any): HistoryRow => ({
        historyId: item.historyId ?? item.HistoryId,
        actorUsername: item.actorUsername ?? item.ActorUsername ?? '',
        targetUsername: item.targetUsername ?? item.TargetUsername ?? '',
        featureKey: item.featureKey ?? item.FeatureKey ?? '',
        action: item.action ?? item.Action ?? '',
        storeCode: item.storeCode ?? item.StoreCode ?? '',
        createdDate: item.createdDate ?? item.CreatedDate ?? '',
    });

    useEffect(() => {
        if (!storeCode) {
            setRows([]);
            return;
        }

        const fetchStaff = async () => {
            setLoading(true);
            try {
                const response = await StoreManagerPermissionQueryService.GetStaff();
                const list = Array.isArray(response) ? response : response?.data ?? response?.items ?? [];
                setRows(Array.isArray(list) ? list.map(mapStaffItem) : []);
            } catch {
                setRows([]);
                Swal.fire({
                    title: t('general.error'),
                    text: t('storeManagerPermission.loadError'),
                    icon: 'error',
                });
            } finally {
                setLoading(false);
            }
        };

        fetchStaff();
    }, [featureKey, storeCode]);

    const fetchUserHistory = async (
        targetApplicationUserId: number,
        pageNumber: number,
        pageSize: number
    ) => {
        setHistoryLoading(true);
        try {
            const response = await StoreManagerPermissionQueryService.GetHistory({
                TargetApplicationUserId: targetApplicationUserId,
                PageNumber: pageNumber,
                PageSize: pageSize,
            });
            const items = response?.items ?? response?.Items ?? (Array.isArray(response) ? response : []);
            setHistoryRows(Array.isArray(items) ? items.map(mapHistoryItem) : []);
            setHistoryTotalCount(response?.totalCount ?? response?.TotalCount ?? items?.length ?? 0);
        } catch {
            setHistoryRows([]);
            setHistoryTotalCount(0);
            Swal.fire({
                title: t('general.error'),
                text: t('storeManagerPermission.loadError'),
                icon: 'error',
            });
        } finally {
            setHistoryLoading(false);
        }
    };

    const openUserHistory = async (row: StaffRow) => {
        setHistoryUser(row);
        setHistoryPage(1);
        setHistoryPageSize(10);
        setHistoryOpen(true);
        await fetchUserHistory(row.applicationUserId, 1, 10);
    };

    const closeUserHistory = () => {
        setHistoryOpen(false);
        setHistoryUser(null);
        setHistoryRows([]);
        setHistoryTotalCount(0);
    };

    const handleGrantChange = async (row: StaffRow, enabled: boolean) => {
        if (!storeCode) {
            return;
        }

        setRows((prev) =>
            prev.map((r) =>
                r.applicationUserId === row.applicationUserId ? { ...r, isUpdating: true, hasGrant: enabled } : r
            )
        );

        try {
            await StoreManagerPermissionCommandService.SetGrant({
                TargetApplicationUserId: row.applicationUserId,
                Enabled: enabled,
            });
            const response = await StoreManagerPermissionQueryService.GetStaff();
            const list = Array.isArray(response) ? response : response?.data ?? response?.items ?? [];
            setRows(Array.isArray(list) ? list.map(mapStaffItem) : []);
            Swal.fire({
                title: t('general.success'),
                text: t('storeManagerPermission.saveSuccess'),
                icon: 'success',
                timer: 1500,
                showConfirmButton: false,
            });
        } catch {
            setRows((prev) =>
                prev.map((r) =>
                    r.applicationUserId === row.applicationUserId
                        ? { ...r, hasGrant: !enabled, isUpdating: false }
                        : r
                )
            );
            Swal.fire({
                title: t('general.error'),
                text: t('storeManagerPermission.saveError'),
                icon: 'error',
            });
            return;
        }
    };

    const formatDateTime = (value: string | null) => {
        if (!value) {
            return '-';
        }
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) {
            return '-';
        }
        return date.toLocaleString(i18n.language, {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    const formatAction = (action: string) => {
        if (action === 'Grant') {
            return t('storeManagerPermission.actionGrant');
        }
        if (action === 'Revoke') {
            return t('storeManagerPermission.actionRevoke');
        }
        return action || '-';
    };

    const columns: ExtendedGridColDef[] = [
        {
            field: 'username',
            headerName: t('storeManagerPermission.username'),
            flex: 1,
            minWidth: 160,
            sortable: false,
        },
        {
            field: 'title',
            headerName: t('storeManagerPermission.titleColumn'),
            flex: 1,
            minWidth: 160,
            sortable: false,
        },
        {
            field: 'grantedByUsername',
            headerName: t('storeManagerPermission.grantedBy'),
            flex: 1,
            minWidth: 140,
            sortable: false,
            renderCell: (params) => (params.row.hasGrant && params.value ? params.value : '-'),
        },
        {
            field: 'grantedAt',
            headerName: t('storeManagerPermission.grantedAt'),
            flex: 1,
            minWidth: 160,
            sortable: false,
            renderCell: (params) =>
                params.row.hasGrant ? formatDateTime(params.value) : '-',
        },
        {
            field: 'hasGrant',
            headerName: t('storeManagerPermission.grant'),
            width: 140,
            sortable: false,
            renderCell: (params) => (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Switch
                        checked={!!params.row.hasGrant}
                        disabled={!!params.row.isUpdating || !storeCode}
                        onChange={(e) => handleGrantChange(params.row, e.target.checked)}
                    />
                    {params.row.isUpdating && <CircularProgress size={16} />}
                </Box>
            ),
        },
        {
            field: 'history',
            headerName: t('storeManagerPermission.viewHistory'),
            width: 120,
            sortable: false,
            renderCell: (params) => (
                <Tooltip title={t('storeManagerPermission.userHistoryTooltip')}>
                    <IconButton
                        size="small"
                        color="primary"
                        disabled={!storeCode}
                        onClick={() => openUserHistory(params.row)}
                    >
                        <HistoryIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            ),
        },
    ];

    const historyColumns: ExtendedGridColDef[] = [
        {
            field: 'actorUsername',
            headerName: t('storeManagerPermission.actor'),
            flex: 1,
            minWidth: 120,
            sortable: false,
        },
        {
            field: 'featureKey',
            headerName: t('storeManagerPermission.feature'),
            flex: 1,
            minWidth: 100,
            sortable: false,
        },
        {
            field: 'action',
            headerName: t('storeManagerPermission.action'),
            width: 110,
            sortable: false,
            renderCell: (params) => (
                <Chip
                    size="small"
                    label={formatAction(params.value)}
                    color={params.value === 'Grant' ? 'success' : 'default'}
                    variant="outlined"
                />
            ),
        },
        {
            field: 'storeCode',
            headerName: t('storeManagerPermission.store'),
            width: 100,
            sortable: false,
        },
        {
            field: 'createdDate',
            headerName: t('storeManagerPermission.date'),
            flex: 1,
            minWidth: 150,
            sortable: false,
            renderCell: (params) => formatDateTime(params.value),
        },
    ];

    return (
        <SCUIContent>
            <Box sx={{ mb: 2 }}>
                <SCUIBreadCrumb
                    title={t('storeManagerPermission.title')}
                    componentLink={PageUrl.storeManagerStaffPermission.url}
                />
            </Box>
            <Box sx={{ position: 'relative', minHeight: '100%' }}>
                <Box
                    sx={{
                        position: 'absolute',
                        top: -24,
                        left: -24,
                        right: -24,
                        height: '4px',
                        zIndex: 9999,
                    }}
                >
                    <SCUILoadingLinear display={loading} />
                </Box>

                {!storeCode && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        {t('storeManagerPermission.storeCodeMissing')}
                    </Alert>
                )}

                {storeCode && (
                    <Box sx={{ mb: 2 }}>
                        <Alert severity="info">
                            {t('storeManagerPermission.featureStoreReturn')} ({featureKey}) — {storeCode}
                        </Alert>
                    </Box>
                )}

                {storeCode && !loading && rows.length === 0 && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                        {t('storeManagerPermission.noData')}
                    </Alert>
                )}

                <SCUICustomDataGrid
                    rows={rows}
                    columns={columns}
                    loading={loading}
                    getRowId={(row: StaffRow) => row.applicationUserId}
                    autoHeight
                    hideFooter={rows.length === 0}
                />
            </Box>

            <Dialog open={historyOpen} onClose={closeUserHistory} fullWidth maxWidth="md">
                <DialogTitle>
                    {t('storeManagerPermission.userHistoryTitle', {
                        username: historyUser?.username ?? '',
                    })}
                </DialogTitle>
                <DialogContent dividers>
                    {storeCode && (
                        <Alert severity="info" sx={{ mb: 2 }}>
                            {t('storeManagerPermission.historyStoreScopeHint', { storeCode })}
                        </Alert>
                    )}
                    {!historyLoading && historyRows.length === 0 && (
                        <Alert severity="info" sx={{ mb: 2 }}>
                            {t('storeManagerPermission.historyEmpty')}
                        </Alert>
                    )}
                    <SCUICustomDataGrid
                        rows={historyRows}
                        columns={historyColumns}
                        loading={historyLoading}
                        getRowId={(row: HistoryRow) => row.historyId}
                        autoHeight
                        pagination
                        paginationModeChange={true}
                        rowCount={historyTotalCount}
                        page={historyPage - 1}
                        pageSize={historyPageSize}
                        onPageChange={(newPage: number) => {
                            const nextPage = newPage + 1;
                            setHistoryPage(nextPage);
                            if (historyUser) {
                                fetchUserHistory(historyUser.applicationUserId, nextPage, historyPageSize);
                            }
                        }}
                        onPageSizeChange={(newSize: number) => {
                            setHistoryPageSize(newSize);
                            setHistoryPage(1);
                            if (historyUser) {
                                fetchUserHistory(historyUser.applicationUserId, 1, newSize);
                            }
                        }}
                        rowsPerPageOptions={[10, 20, 50]}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeUserHistory}>{t('general.close')}</Button>
                </DialogActions>
            </Dialog>
        </SCUIContent>
    );
};
